// GEOBAJA — Snapshots persistentes (IndexedDB) para arranque sin señal
//
// Los snapshots SWR de los módulos (prewarm de concreto/compactación y los
// catálogos core del AppShell) viven en memoria: un reinicio de la app en
// campo los perdía y sin red la tablet quedaba en blanco. Este módulo los
// respalda en IndexedDB y los re-hidrata al boot, con acceso SÍNCRONO vía
// espejo en memoria (los getters de los prewarm corren durante el render).
//
// - DB propia ('geobaja-snapshots') separada del outbox ('geobaja-offline'):
//   cero coordinación de versiones entre módulos.
// - set() es write-through: memoria al instante, IDB en segundo plano.
// - get() es síncrono contra el espejo; devuelve { at, data } y el CALLER
//   aplica su política de frescura (los prewarm: 10 min online, cualquier
//   edad sin red — datos de la mañana valen más que una pantalla vacía).
// - clearAll() en signOut: los snapshots no deben cruzar de usuario en una
//   tablet compartida. El outbox NO se limpia ahí — las capturas pendientes
//   sobreviven al logout.

const DB_NAME = 'geobaja-snapshots';
const DB_VERSION = 1;
const STORE = 'kv';

const mem = new Map();
let _db = null;
// Compuerta de escritura: clearAll() (logout) la cierra para que los fetches
// aún en vuelo del usuario saliente no re-persistan sus datos; el siguiente
// login la reabre (enable). Ver App.js.
let _enabled = true;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE, { keyPath: 'key' });
    };
    req.onsuccess = () => {
      const db = req.result;
      db.onversionchange = () => { try { db.close(); } catch (e) {} _db = null; };
      resolve(db);
    };
    req.onerror = () => reject(req.error);
  });
}

async function hydrate() {
  if (typeof window === 'undefined' || !('indexedDB' in window)) return;
  try {
    _db = await openDB();
    const rows = await new Promise((resolve, reject) => {
      const t = _db.transaction(STORE, 'readonly');
      const rq = t.objectStore(STORE).getAll();
      rq.onsuccess = () => resolve(rq.result || []);
      rq.onerror = () => reject(rq.error);
    });
    // La memoria gana: si algún módulo ya escribió antes de terminar la
    // hidratación (carrera improbable), no pisarlo con lo persistido.
    rows.forEach((r) => { if (!mem.has(r.key)) mem.set(r.key, { at: r.at, data: r.data }); });
  } catch (e) { /* IDB inaccesible (modo privado): opera solo-memoria */ }
}

/** Promesa de hidratación — await antes de decidir "no hay snapshot" en frío. */
const ready = hydrate();

/** Write-through: memoria síncrona + IDB tras `ready` (las escrituras
 *  tempranas — shell:core justo tras login — no se pierden mientras abre). */
function set(key, data) {
  if (!_enabled) return;
  const rec = { at: Date.now(), data };
  mem.set(key, rec);
  ready.then(() => {
    if (!_db || !_enabled) return;
    try {
      const t = _db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).put({ key, ...rec });
    } catch (e) { /* cuota/DB cerrada: el espejo en memoria sigue sirviendo */ }
  });
}

/** Síncrono. Devuelve { at, data } o null; la política de edad es del caller. */
function get(key) {
  return mem.get(key) || null;
}

/** Borra una llave (invalidaciones puntuales: entregas, publicaciones). */
function remove(key) {
  mem.delete(key);
  ready.then(() => {
    if (!_db) return;
    try {
      const t = _db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).delete(key);
    } catch (e) {}
  });
}

/** Borra todo (signOut) y CIERRA la compuerta de escritura: los fetches en
 *  vuelo del usuario saliente ya no pueden re-persistir sus datos. */
function clearAll() {
  _enabled = false;
  mem.clear();
  ready.then(() => {
    if (!_db) return;
    try {
      const t = _db.transaction(STORE, 'readwrite');
      t.objectStore(STORE).clear();
    } catch (e) {}
  });
}

/** Reabre la compuerta (siguiente login). */
function enable() { _enabled = true; }

export const snapStore = { ready, set, get, remove, clearAll, enable };
